import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import SignatureCanvas from "./SignatureCanvas";
import { DebugButton } from "./DebugButton";
import { FormData, FormType, FormConfig } from "../types";
import {
  getFormDataFromParams,
  generateShareableUrl,
  getCurrentDate,
  getCurrentTime,
} from "../utils/formUtils";
import { ophaalFormConfig } from "../config/forms";
import { submitVehicleForm, type NetworkError } from "../utils/api";
import Cookies from "js-cookie";
import FormButtons from "./FormButtons";
import PhotoModeToggle from "./PhotoModeToggle";
import FormSection from "./FormSection";
import { useFormChange } from "../contexts/useFormChange";
import {
  hasFormDataChanged,
  hasFormDataChangedForPersistence,
  createInitialFormData,
} from "../utils/formChangeUtils";
import { db } from "../db";
import {
  deserializeFiles,
  refreshFile,
  type SerializedFile,
} from "../utils/imageUtils";
import { t } from "../locales/nl.ts";

interface VehicleFormProps {
  formType: FormType;
  formConfig?: FormConfig;
  onSectionChange?: (_section: string) => void;
}

const isSerializedFile = (value: unknown): value is SerializedFile => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as SerializedFile;
  return (
    typeof candidate.name === "string" &&
    typeof candidate.type === "string" &&
    typeof candidate.lastModified === "number" &&
    typeof candidate.data === "string"
  );
};

// Helper function to get all field names from form config
const getAllFieldNames = (config: FormConfig): string[] => {
  const fieldNames: string[] = [];
  config.forEach((section) => {
    if (section.fields) {
      section.fields.forEach((field) => {
        fieldNames.push(field.name);
      });
    }
  });
  return fieldNames;
};

const VehicleForm: React.FC<VehicleFormProps> = ({
  formType,
  formConfig,
  onSectionChange,
}) => {
  const navigate = useNavigate();
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [imageErrors, setImageErrors] = useState<Record<string, string>>({});
  const [signatureErrors, setSignatureErrors] = useState<
    Record<string, string>
  >({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<NetworkError | null>(null);
  const [submitProgress, setSubmitProgress] = useState<number>(0);
  const currentConfig = formConfig || ophaalFormConfig;
  const [showSharePopover, setShowSharePopover] = useState(false);
  const [isCameraMode, setIsCameraMode] = useState(() => {
    const savedMode = Cookies.get("photoInputMode");
    return savedMode === "camera";
  });
  const sectionRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Track if form data was loaded from URL
  // Note: getFormDataFromParams always returns photos/signatures as empty objects,
  // so we check for actual form field values
  const hasDataFromUrl = (() => {
    const paramsData = getFormDataFromParams(currentConfig);
    const formFieldKeys = Object.keys(paramsData).filter(
      (key) => key !== "photos" && key !== "signatures",
    );
    return formFieldKeys.some((key) => {
      const value = paramsData[key];
      return value !== undefined && value !== null && value !== "";
    });
  })();

  // Track initial form data for change detection
  const [initialFormData, setInitialFormData] = useState<FormData | null>(null);
  const { setHasUnsavedChanges, resetFormState } = useFormChange();

  const submitMutation = useMutation({
    mutationFn: (data: FormData) =>
      submitVehicleForm(data, formType, setSubmitProgress),
    onSuccess: async () => {
      // Clear saved draft
      try {
        await db.formData.delete(formType);
      } catch (error) {
        console.error("Error clearing saved draft:", error);
      }

      // Reset the initial form data since form is successfully submitted
      setInitialFormData(null);
      resetFormState();
      navigate("/confirmation");
    },
    onError: (error: NetworkError) => {
      console.error("Error submitting form:", error);
      setSubmitError(error);
    },
  });

  const buildInitialFormData = (useUrlParams: boolean): FormData => {
    const paramsData = useUrlParams ? getFormDataFromParams(currentConfig) : {};
    const searchParams = new URLSearchParams(window.location.search);
    const hasSharedData = useUrlParams && Array.from(searchParams).length > 0;

    // Initialize base form data
    const initialData: FormData = {
      photos: (paramsData as any).photos || {},
      signatures: (paramsData as any).signatures || {},
    };

    // Add all fields from the form configuration
    currentConfig.forEach((section) => {
      if (section.fields) {
        section.fields.forEach((field) => {
          // Handle special cases for date and time fields
          if (field.type === "date") {
            initialData[field.name] = hasSharedData
              ? (paramsData as any).date || getCurrentDate()
              : getCurrentDate();
          } else if (field.type === "time") {
            initialData[field.name] = hasSharedData
              ? (paramsData as any).time || getCurrentTime()
              : getCurrentTime();
          } else {
            // For all other fields, use the value from params, field's default value, or empty string
            initialData[field.name] =
              (paramsData as any)[field.name] || field.defaultValue || "";
          }
        });
      }
    });

    return initialData;
  };

  // Initialize form with current date/time and URL parameters
  const [formData, setFormData] = useState<FormData>(() => {
    return buildInitialFormData(true);
  });

  // Track last saved data to avoid unnecessary writes
  const lastSavedDataRef = useRef<FormData | null>(null);
  const isLoadingRef = useRef(false);
  const hasAttemptedLoadRef = useRef(false);
  const hasLoadedDraftRef = useRef(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // Mirror formData in a ref so the beforeunload handler can access the
  // latest value without depending on a stale closure.
  const formDataRef = useRef<FormData>(formData);
  formDataRef.current = formData;

  // Apply saved draft when available and no URL params (prioritize URL params if they exist)
  useEffect(() => {
    const loadSavedData = async () => {
      isLoadingRef.current = true;
      try {
        const currentParams = window.location.search;
        const searchParams = new URLSearchParams(currentParams);
        const hasSharedData = Array.from(searchParams).length > 0;

        // Get draft directly from DB
        const savedDraft = await db.formData.get(formType);

        // Only apply a saved draft once on initial load, and only when there
        // is no shared data in the URL (URL params take precedence).
        const shouldApplyDraft =
          !!savedDraft &&
          !hasLoadedDraftRef.current &&
          (!hasSharedData || savedDraft.queryString === currentParams);

        if (shouldApplyDraft) {
          // Deserialize photos from saved draft.
          // Individual file failures are tolerated so that stale blob
          // references (common on iOS after a browser restart) don't
          // prevent the rest of the draft (text fields, signatures)
          // from being restored.
          const deserializedPhotos: Record<string, File[]> = {};
          if (savedDraft.data.photos) {
            for (const [key, storedFiles] of Object.entries(
              savedDraft.data.photos,
            )) {
              if (!Array.isArray(storedFiles)) continue;
              if (storedFiles.length === 0) {
                deserializedPhotos[key] = [];
                continue;
              }

              try {
                // Backward compatibility:
                // - new drafts store File[] directly
                // - old drafts may still store SerializedFile[]
                const first = storedFiles[0];
                if (first instanceof File) {
                  const results = await Promise.all(
                    (storedFiles as File[]).map(async (f) => {
                      try {
                        return await refreshFile(f);
                      } catch {
                        return null;
                      }
                    }),
                  );
                  deserializedPhotos[key] = results.filter(
                    (f): f is File => f !== null,
                  );
                } else if (isSerializedFile(first)) {
                  deserializedPhotos[key] = await deserializeFiles(
                    storedFiles as SerializedFile[],
                  );
                } else {
                  deserializedPhotos[key] = [];
                }
              } catch (err) {
                console.warn(
                  `Failed to deserialize photos for "${key}":`,
                  err,
                );
                deserializedPhotos[key] = [];
              }
            }
          }

          setFormData((prev) => {
            const mergedData: FormData = {
              ...prev,
              ...savedDraft.data,
              photos: deserializedPhotos,
              signatures: savedDraft.data.signatures || prev.signatures,
            };

            // Initialize last saved data ref to avoid immediate re-save.
            // Set hasAttemptedLoadRef and formDataRef here so they are
            // updated atomically with the data — prevents the beforeunload
            // flush from saving stale (empty) initial state over the draft.
            lastSavedDataRef.current = createInitialFormData(mergedData);
            formDataRef.current = mergedData;
            hasAttemptedLoadRef.current = true;
            return mergedData;
          });

          hasLoadedDraftRef.current = true;
        } else {
          // No draft to apply — safe to enable autosave immediately.
          hasAttemptedLoadRef.current = true;
        }
      } catch (error) {
        console.error("Error loading form data:", error);
        // Still enable autosave so the user's work is saved going forward.
        hasAttemptedLoadRef.current = true;
      } finally {
        isLoadingRef.current = false;
        setIsInitializing(false);
      }
    };

    loadSavedData();
  }, [formType, currentConfig]);

  // Auto-save to Dexie
  useEffect(() => {
    const saveToDb = async () => {
      // Skip if initial load hasn't completed yet (prevents race condition)
      if (!hasAttemptedLoadRef.current) {
        return;
      }

      // Skip if no changes from last save
      if (
        lastSavedDataRef.current &&
        !hasFormDataChangedForPersistence(formData, lastSavedDataRef.current)
      ) {
        return;
      }

      try {
        // Always do a full put with in-memory photos. After loading from
        // IndexedDB, photos are refreshed via refreshFile() which creates
        // File objects backed by ArrayBuffers — these survive structured
        // cloning reliably. Dexie's modify() re-writes the entire record
        // including photos read from IDB, which can produce File objects
        // with invalid blob data on subsequent loads.
        const photosForStorage: Record<string, File[]> = {};
        if (formData.photos) {
          for (const [key, files] of Object.entries(formData.photos)) {
            if (Array.isArray(files)) {
              photosForStorage[key] = (
                files as Array<File | SerializedFile>
              ).filter((file): file is File => file instanceof File);
            }
          }
        }

        await db.formData.put({
          formType,
          data: {
            ...formData,
            photos: photosForStorage,
          },
          queryString: window.location.search,
          updatedAt: Date.now(),
        });

        // Update last saved reference
        lastSavedDataRef.current = createInitialFormData(formData);
      } catch (error) {
        console.error("Error saving draft:", error);
      }
    };

    // Debounce save
    const timeoutId = setTimeout(saveToDb, 500);
    return () => clearTimeout(timeoutId);
  }, [formData, formType]);

  // Flush unsaved changes immediately when the page is about to unload or
  // the tab becomes hidden. The debounced autosave above has a 500ms window
  // where changes haven't been persisted yet — if the user refreshes during
  // that window the data is lost. This handler closes that gap by starting
  // an IndexedDB write synchronously; modern browsers allow the transaction
  // to commit even during page teardown.
  useEffect(() => {
    const flushPendingSave = () => {
      if (!hasAttemptedLoadRef.current) return;
      const current = formDataRef.current;
      if (
        lastSavedDataRef.current &&
        !hasFormDataChangedForPersistence(current, lastSavedDataRef.current)
      ) {
        return;
      }

      const photosForStorage: Record<string, File[]> = {};
      if (current.photos) {
        for (const [key, files] of Object.entries(current.photos)) {
          if (Array.isArray(files)) {
            photosForStorage[key] = (
              files as Array<File | SerializedFile>
            ).filter((file): file is File => file instanceof File);
          }
        }
      }

      // Fire-and-forget — we cannot await during unload.
      db.formData.put({
        formType,
        data: {
          ...current,
          photos: photosForStorage,
        },
        queryString: window.location.search,
        updatedAt: Date.now(),
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flushPendingSave();
      }
    };

    window.addEventListener("beforeunload", flushPendingSave);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("beforeunload", flushPendingSave);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [formType]);

  // Initialize the initial form data for change tracking
  useEffect(() => {
    if (!initialFormData) {
      // Add a small delay to ensure form is fully initialized
      const timer = setTimeout(() => {
        setInitialFormData(createInitialFormData(formData));
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [formData, initialFormData]);

  // Check if form has unsaved changes - only if we have initial data to compare against
  const hasUnsavedChanges = initialFormData
    ? hasFormDataChanged(formData, initialFormData)
    : false;

  // Update the context with the current change state
  useEffect(() => {
    setHasUnsavedChanges(hasUnsavedChanges);
  }, [hasUnsavedChanges, setHasUnsavedChanges]);

  // Set initial URL and handle URL updates
  useEffect(() => {
    const params = new URLSearchParams();
    const fieldNames = getAllFieldNames(currentConfig);

    fieldNames.forEach((fieldName: string) => {
      const value = (formData as any)[fieldName];
      if (value && typeof value === "string") {
        params.append(fieldName, value);
      }
    });
  }, [formData, formType, currentConfig]);

  useEffect(() => {
    Cookies.set("photoInputMode", isCameraMode ? "camera" : "gallery", {
      expires: 365,
    });
  }, [isCameraMode]);

  // Add intersection observer for sections
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionTitle = entry.target.getAttribute("data-section");
            if (sectionTitle) {
              onSectionChange?.(sectionTitle);
            }
          }
        });
      },
      {
        rootMargin: "-20% 0px -80% 0px",
        threshold: 0,
      },
    );

    // Observe all sections
    sectionRefs.current.forEach((element) => {
      observer.observe(element);
    });

    return () => observer.disconnect();
  }, [onSectionChange]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handlePhotoChange = (fieldName: string, files: File[]) => {
    setFormData((prev) => ({
      ...prev,
      photos: {
        ...prev.photos,
        [fieldName]: files,
      },
    }));

    // Clear error when files are added
    if (files.length > 0) {
      setImageErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleSignatureChange = (
    name: string,
    signatureData: string | null,
  ) => {
    setFormData((prev) => ({
      ...prev,
      signatures: {
        ...prev.signatures,
        [name]: signatureData,
      },
    }));

    // Clear error when signature is added
    if (signatureData && signatureData.trim() !== "") {
      setSignatureErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newImageErrors: Record<string, string> = {};
    const newSignatureErrors: Record<string, string> = {};
    const newFieldErrors: Record<string, string> = {};
    let isValid = true;
    let firstErrorElement: HTMLElement | null = null;

    // Validate required text fields
    currentConfig.forEach((section) => {
      if (section.fields) {
        section.fields.forEach((field) => {
          if (field.required) {
            const value = formData[field.name];
            if (!value || (typeof value === "string" && value.trim() === "")) {
              newFieldErrors[field.name] = "Dit veld is verplicht";
              isValid = false;
              // Find the first error element
              if (!firstErrorElement) {
                firstErrorElement = document.querySelector(
                  `[data-field="${field.name}"]`,
                );
              }
            }
          }

          // Validate field based on validation rules
          if (field.validation) {
            const value = formData[field.name];
            if (value !== undefined && value !== null && value !== "") {
              // Check min value
              if (field.validation.min !== undefined) {
                const numValue = Number(value);
                if (!isNaN(numValue) && numValue < field.validation.min) {
                  newFieldErrors[field.name] =
                    `Waarde moet minimaal ${field.validation.min} zijn`;
                  isValid = false;
                  if (!firstErrorElement) {
                    firstErrorElement = document.querySelector(
                      `[data-field="${field.name}"]`,
                    );
                  }
                }
              }

              // Check max value
              if (field.validation.max !== undefined) {
                const numValue = Number(value);
                if (!isNaN(numValue) && numValue > field.validation.max) {
                  newFieldErrors[field.name] =
                    `Waarde moet maximaal ${field.validation.max} zijn`;
                  isValid = false;
                  if (!firstErrorElement) {
                    firstErrorElement = document.querySelector(
                      `[data-field="${field.name}"]`,
                    );
                  }
                }
              }

              // Check custom validation
              if (field.validation.custom) {
                const customError = field.validation.custom(value);
                if (customError) {
                  newFieldErrors[field.name] = customError;
                  isValid = false;
                  if (!firstErrorElement) {
                    firstErrorElement = document.querySelector(
                      `[data-field="${field.name}"]`,
                    );
                  }
                }
              }
            }
          }
        });
      }
    });

    // Validate required image fields
    currentConfig.forEach((section) => {
      section.imageSections?.forEach((imageSection) => {
        imageSection.images.forEach((image) => {
          if (
            image.required &&
            (!formData.photos[image.name] ||
              formData.photos[image.name].length === 0)
          ) {
            newImageErrors[image.name] = "Dit veld is verplicht";
            isValid = false;
            // Find the first error element
            if (!firstErrorElement) {
              firstErrorElement = document.querySelector(
                `[data-field="${image.name}"]`,
              );
            }
          }
        });
      });
    });

    // Validate required signature fields
    if (currentConfig.signatures) {
      currentConfig.signatures.fields.forEach((field) => {
        const signature = formData.signatures[field.name];
        if (field.required && (!signature || signature.trim() === "")) {
          newSignatureErrors[field.name] = "Dit veld is verplicht";
          isValid = false;
          // Find the first error element
          if (!firstErrorElement) {
            firstErrorElement = document.querySelector(
              `[data-field="${field.name}"]`,
            );
          }
        }
      });
    }

    setImageErrors(newImageErrors);
    setSignatureErrors(newSignatureErrors);
    setFieldErrors(newFieldErrors);

    // Scroll to first error if any
    if (!isValid && firstErrorElement) {
      (firstErrorElement as HTMLElement).scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    // Check if user is offline
    if (!navigator.onLine) {
      setSubmitError({
        type: "network",
        message: t.errors.offline,
      });
      return;
    }

    if (!validateForm()) {
      return;
    }

    submitMutation.mutate(formData);
  };

  const handleShare = () => {
    const url = generateShareableUrl(
      formType,
      formData,
      formConfig || ophaalFormConfig,
    );
    setShareUrl(url);
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setShowSharePopover(true);
        setTimeout(() => setShowSharePopover(false), 2500);
      })
      .catch((err) => {
        console.error("Fout bij kopiëren naar klembord:", err);
      });
  };

  const handleDebugFill = async (data: Record<string, any>) => {
    // Create a new FormData object with the current state
    const newFormData: FormData = {
      ...formData,
      ...data,
      photos: {
        ...formData.photos,
        ...data.photos,
      },
      signatures: {
        ...formData.signatures,
        ...data.signatures,
      },
    };

    // Update the form state
    setFormData(newFormData);

    // Clear any existing errors
    setImageErrors({});
    setSignatureErrors({});
    setSubmitError(null);
  };

  const handleModeToggle = () => {
    setIsCameraMode((prevMode) => !prevMode);
  };

  const handleClearForm = async () => {
    if (window.confirm(t.clearFormConfirmation)) {
      const hasSharedData = window.location.search.length > 0;
      const clearedData = buildInitialFormData(hasSharedData);

      // Photos/signatures are never provided via query params, so clearing should always wipe them.
      clearedData.photos = {};
      clearedData.signatures = {};

      // Clear DB
      try {
        await db.formData.delete(formType);
      } catch (error) {
        console.error("Error clearing draft:", error);
      }

      // Update state
      setFormData(clearedData);

      // Reset change-tracking baseline so clearing doesn't count as "unsaved changes"
      setInitialFormData(createInitialFormData(clearedData));
      resetFormState();

      // Clear errors
      setFieldErrors({});
      setImageErrors({});
      setSignatureErrors({});
      setSubmitError(null);

      // Reset initialization state to allow saving new clean draft
      // Set the last-saved baseline to the cleared state so we don't immediately auto-save an empty draft.
      // A new draft will be created once the user actually changes something again.
      lastSavedDataRef.current = createInitialFormData(clearedData);
    }
  };

  if (isInitializing) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <form
      enterKeyHint={"send"}
      onSubmit={handleSubmit}
      className="space-y-6 mb-12"
    >
      {!hasDataFromUrl && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleClearForm}
            className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            {t.clearForm}
          </button>
        </div>
      )}
      {currentConfig.map((section) => (
        <div
          key={section.section}
          ref={(el) => {
            if (el) {
              sectionRefs.current.set(section.section, el);
            }
          }}
          data-section={section.section}
        >
          <FormSection
            section={section}
            formData={formData}
            imageErrors={imageErrors}
            fieldErrors={fieldErrors}
            onInputChange={handleInputChange}
            onPhotoChange={handlePhotoChange}
            isCameraMode={isCameraMode}
          />
        </div>
      ))}

      {currentConfig.signatures && (
        <div className="card">
          <h2 className="text-lg font-medium mb-4">
            {currentConfig.signatures.title}
          </h2>

          {currentConfig.signatures.fields.map((field) => (
            <div key={field.name} data-field={field.name}>
              <SignatureCanvas
                label={field.label}
                onSignatureChange={(signature) =>
                  handleSignatureChange(field.name, signature)
                }
                required={field.required}
                error={signatureErrors[field.name]}
                value={formData.signatures[field.name]}
              />
              {/* Show info text after customer signature */}
              {field.name === "customer_signature" &&
                currentConfig.signatures?.infoText && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-start space-x-2">
                      <svg
                        className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <p className="text-sm text-blue-800">
                        {currentConfig.signatures.infoText}
                      </p>
                    </div>
                  </div>
                )}
            </div>
          ))}
        </div>
      )}

      <FormButtons
        onSubmit={handleSubmit}
        onShare={handleShare}
        isSubmitting={submitMutation.isPending}
        shareUrl={shareUrl}
        showSharePopover={showSharePopover}
        setShowSharePopover={setShowSharePopover}
        submitError={submitError}
        submitProgress={submitProgress}
        hideShareButton={hasDataFromUrl}
      />

      <DebugButton
        fields={[
          ...currentConfig.flatMap((section) => section.fields || []),
          ...currentConfig.flatMap(
            (section) =>
              section.imageSections?.flatMap((imgSection) =>
                imgSection.images.map((img) => ({
                  name: img.name,
                  type: "file",
                  required: img.required,
                })),
              ) || [],
          ),
          ...(currentConfig.signatures?.fields.map((field) => ({
            name: field.name,
            type: "signature",
            required: field.required,
          })) || []),
        ]}
        onFill={handleDebugFill}
      />

      <PhotoModeToggle
        isCameraMode={isCameraMode}
        onToggle={handleModeToggle}
      />
    </form>
  );
};

export default VehicleForm;
